import { DefaultError, useMutation, UseMutationResult } from '@tanstack/react-query';
import { skus } from '@api/apis';
import { IHash } from 'types/base.types';

/* utils */
import { StatusErrors } from '@constants/base';
import { deriveResponseStatus } from '@utils/object';

/* Keys */
import { Logger } from '@utils/logger';

/**
 * @remark
 * Pass in options to override defaults, if needed.
 * @params: returned:
 * data, isLoading, isError, error, status, isFetching, refetch
 */
interface ISkuSkuPrice {
  skuId: number;
  config?: IHash<any>;
}

/**
 * We use this to override the default pricing for the SSG pages.
 *
 * @param {number} skuId
 * @param {IHash<any> | undefined} config
 * @returns {UseMutationResult<any, DefaultError, void, unknown>}
 */
const useSkuMutationPrice = ({ skuId, config = {} }: ISkuSkuPrice): UseMutationResult<any, DefaultError, void, unknown> =>
  useMutation({
    mutationKey: ['sku', 'price', skuId],
    mutationFn: async () => {
      const response = await skus.skuPrice({ id: skuId });

      if (StatusErrors.has(deriveResponseStatus(response)) || response.err) throw response;

      return response.data;
    },
    /* We should move this out to the global config and log there */
    onError: error => {
      Logger('Sku Price')(error as Error);
    },
    ...config,
  });

export default useSkuMutationPrice;
